import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { signInWithCustomToken, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { resetLoginReminders } from '../../components/LoginReminders/useMissingDocuments';
import { hasDevicePasskey, loginWithPasskey, passkeyErrorReason, registerPasskey, setDevicePasskey } from './passkeyClient';

const LAST_EMAIL_KEY = 'lastLoggedEmail';

const readLastEmail = () => {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) || '';
  } catch (error) {
    return '';
  }
};

/**
 * Everything that happens between "Giriş Yap" and the Hall:
 *  1. face login only: the face scan (camera photo for the login record),
 *  2. sign-in: e-mail + password, or a passkey token (face login),
 *  3. the employee must exist and be active, and must have accepted KVKK,
 *  4. if the user asked for face login on this device: register the face,
 *  5. login record + local session data, then to the Hall.
 * `stage` says what the card shows: form | scan | enroll.
 */
const useLoginFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(readLastEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState('form');
  const [setupIntent, setSetupIntent] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [kvkkOpen, setKvkkOpen] = useState(false);
  const [kvkkSaving, setKvkkSaving] = useState(false);
  const photoRef = useRef(null);
  const afterScanRef = useRef(null);
  // The signed-in employee between the checks and the end of the login
  const pendingRef = useRef(null);
  const setupIntentRef = useRef(false);
  setupIntentRef.current = setupIntent;

  useEffect(() => () => {
    afterScanRef.current = null;
  }, []);

  const completeLogin = useCallback(async () => {
    const { employeeDoc, employeeData, loginEmail } = pendingRef.current;
    try {
      // Always store a login history entry; the photo is included when one
      // was taken (camera permission may be denied or capture may fail)
      try {
        const loginHistoryRef = collection(db, 'Employees', employeeDoc.id, 'loginHistory');
        await setDoc(doc(loginHistoryRef), {
          timestamp: new Date(),
          snapshot: photoRef.current || null,
          device: navigator.userAgent,
        });
        await updateDoc(doc(db, 'Employees', employeeDoc.id), { lastLogin: new Date() });
      } catch (historyError) {
        console.error('Error saving login history:', historyError);
      }

      localStorage.setItem('employeeId', employeeDoc.id);
      localStorage.setItem('employeeData', JSON.stringify({
        name: employeeData.eName,
        surname: employeeData.eSurname,
        email: employeeData.eMail,
      }));
      // Remember the last user for the next login
      localStorage.setItem(LAST_EMAIL_KEY, loginEmail);
      // Her girişte hatırlatmalar (okunmamış SOP, eksik belge) yeniden çıksın
      resetLoginReminders();

      navigate('/hall', {
        state: { email: employeeData.eMail, userId: employeeDoc.id, name: employeeData.eName },
      });
    } catch (completeError) {
      console.error('Complete login error:', completeError);
      setError(t('login.errors.incomplete'));
      setIsLoading(false);
    }
  }, [navigate, t]);

  // KVKK accepted (or already given): register the face if asked, then finish
  const continueAfterChecks = useCallback(async () => {
    if (setupIntentRef.current && !pendingRef.current.viaPasskey) {
      setEnrollError('');
      setStage('enroll');
      setIsLoading(false);
      return;
    }
    await completeLogin();
  }, [completeLogin]);

  /**
   * Signs in and checks the employee. `employeeId` (face login) reads the
   * record directly; otherwise it is looked up by the typed e-mail, as before.
   */
  const proceedWithLogin = useCallback(async ({ signIn, employeeId, loginEmail, viaPasskey }) => {
    try {
      await signIn();

      let employeeDoc;
      if (employeeId) {
        const snap = await getDoc(doc(db, 'Employees', employeeId));
        if (!snap.exists()) throw new Error('Employee not found');
        employeeDoc = snap;
      } else {
        const snap = await getDocs(query(collection(db, 'Employees'), where('eMail', '==', loginEmail)));
        if (snap.empty) throw new Error('User not found in Employees collection');
        [employeeDoc] = snap.docs;
      }
      const employeeData = employeeDoc.data();

      if (employeeData.Active !== 1) {
        await auth.signOut();
        setError(t('login.errors.inactive'));
        setIsLoading(false);
        return;
      }

      pendingRef.current = { employeeDoc, employeeData, loginEmail, viaPasskey };
      if (employeeData.kvkkConsent !== true) {
        setKvkkOpen(true);
        setIsLoading(false);
        return;
      }
      await continueAfterChecks();
    } catch (loginError) {
      console.error('Login error:', loginError);
      // Never leave a half-finished sign-in behind
      if (auth.currentUser) auth.signOut().catch(() => {});
      setError(viaPasskey ? t('login.passkey.failed') : t('login.errors.invalid'));
      setIsLoading(false);
    }
  }, [continueAfterChecks, t]);

  const runScan = (next) => {
    photoRef.current = null;
    afterScanRef.current = next;
    setStage('scan');
  };

  const handleScanDone = useCallback((photo) => {
    photoRef.current = photo;
    setStage('form');
    const next = afterScanRef.current;
    afterScanRef.current = null;
    if (next) next();
  }, []);

  const submitPassword = (event) => {
    event.preventDefault();
    if (isLoading) return;
    setError('');
    setIsLoading(true);
    // Şifreyle girişte yüz tarama ekranı açılmaz; giriş kaydı fotoğrafsız tutulur
    photoRef.current = null;
    proceedWithLogin({
      signIn: () => signInWithEmailAndPassword(auth, email, password),
      loginEmail: email,
      viaPasskey: false,
    });
  };

  const loginWithFace = async () => {
    if (passkeyBusy || isLoading) return;
    setError('');
    setPasskeyBusy(true);
    try {
      const result = await loginWithPasskey();
      setIsLoading(true);
      runScan(() => proceedWithLogin({
        signIn: () => signInWithCustomToken(auth, result.token),
        employeeId: result.employeeId,
        loginEmail: result.email,
        viaPasskey: true,
      }));
    } catch (passkeyError) {
      const reason = passkeyErrorReason(passkeyError);
      if (reason !== 'cancelled') console.error('Face login error:', passkeyError);
      if (reason === 'unknown') setSetupIntent(true);
      setError(t(`login.passkey.${['cancelled', 'unknown', 'inactive', 'expired', 'unsupported'].includes(reason) ? reason : 'failed'}`));
    } finally {
      setPasskeyBusy(false);
    }
  };

  /** "Yüz tanıma ile giriş": face login where it is set up, otherwise the set-up path. */
  const startFaceLogin = (force = false) => {
    if (!force && !hasDevicePasskey()) {
      setError('');
      setSetupIntent(true);
      return;
    }
    loginWithFace();
  };

  const cancelSetup = () => setSetupIntent(false);

  const enrollFace = async () => {
    if (enrollBusy) return;
    setEnrollBusy(true);
    setEnrollError('');
    try {
      await registerPasskey(pendingRef.current.employeeDoc.id);
      toast.success(t('login.enroll.success'));
    } catch (enrollFailure) {
      const reason = passkeyErrorReason(enrollFailure);
      if (reason === 'exists') {
        setDevicePasskey(true);
        toast.info(t('login.enroll.exists'));
      } else {
        if (reason !== 'cancelled') console.error('Face enrollment error:', enrollFailure);
        const key = { cancelled: 'cancelled', 'fresh-login': 'freshLogin', unsupported: 'unsupported' }[reason] || 'failed';
        setEnrollError(t(`login.enroll.${key}`));
        setEnrollBusy(false);
        return;
      }
    }
    setEnrollBusy(false);
    setIsLoading(true);
    await completeLogin();
  };

  const skipEnroll = async () => {
    setIsLoading(true);
    await completeLogin();
  };

  const acceptKvkk = async () => {
    setKvkkSaving(true);
    try {
      await updateDoc(doc(db, 'Employees', pendingRef.current.employeeDoc.id), {
        kvkkConsent: true,
        kvkkConsentDate: new Date(),
      });
      setKvkkOpen(false);
      setIsLoading(true);
      await continueAfterChecks();
    } catch (kvkkError) {
      console.error('Error saving KVKK consent:', kvkkError);
      setError(t('login.errors.kvkkSave'));
      toast.error(t('login.errors.kvkkSave'));
    } finally {
      setKvkkSaving(false);
    }
  };

  const rejectKvkk = () => {
    setKvkkOpen(false);
    pendingRef.current = null;
    setError(t('login.errors.kvkkRejected'));
    setIsLoading(false);
    auth.signOut();
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    error,
    isLoading,
    stage,
    setupIntent,
    passkeyBusy,
    enrollBusy,
    enrollError,
    kvkkOpen,
    kvkkSaving,
    submitPassword,
    handleScanDone,
    startFaceLogin,
    cancelSetup,
    enrollFace,
    skipEnroll,
    acceptKvkk,
    rejectKvkk,
  };
};

export default useLoginFlow;
